import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import {
  ArrowLeft,
  Check,
  Eye,
  FileSpreadsheet,
  Inbox,
  Loader2,
  Printer,
  Send,
} from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber, formatTaka, pluralize } from '@/utils/number';
import { PcmpHoldActionButtons } from '../components/PcmpHoldActions/PcmpHoldActionButtons';
import { PCMP_PERMISSION } from '../api/pcmpAccess.api';
import {
  downloadPcmpRedisburseBatchXlsx,
  pcmpRedisburseBatchQueryOptions,
} from '../api/pcmpAccounts.api';
import {
  PcmpBatchAmountFact,
  PcmpBatchFact,
} from '../components/PcmpAccountsBatch/PcmpAccountsBatchFacts';
import { PcmpAccountsActionButtons } from '../components/PcmpAccountsActions/PcmpAccountsActionButtons';
import {
  PcmpRedisburseBulkDialog,
  type PcmpRedisburseBulkTarget,
} from '../components/PcmpAccountsActions/PcmpRedisburseBulkDialog';
import { getPcmpAccountsColumns } from '../components/PcmpAccountsQueue/PcmpAccountsColumns';
import { PcmpBatchStepCell } from '../components/PcmpBatch/PcmpBatchCells';
import { PcmpSelectionBar } from '../components/PcmpBatch/PcmpSelectionBar';
import { PcmpQueueFilterBar } from '../components/PcmpQueue/PcmpQueueFilterBar';
import { pcmpSelectColumn } from '../components/PcmpQueue/pcmpSelectColumn';
import {
  PCMP_REDISBURSE_ACTION_COPY,
  PCMP_REDISBURSE_VIEW_LABELS,
} from '../constants/pcmpAccounts.constants';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../constants/pcmpQueue.constants';
import { useDownloadPcmpBatchXlsx } from '../hooks/useDownloadPcmpBatchXlsx';
import { usePcmpRedisburseBatchFilters } from '../hooks/usePcmpAccountsBatchFilters';
import { usePcmpPermissions } from '../hooks/usePcmpPermissions';
import { usePcmpRowSelection } from '../hooks/usePcmpRowSelection';
import {
  PCMP_ACCOUNTS_BATCH_VIEWS,
  type PcmpAccountsBatchView,
  type PcmpRedisburseBatchItem,
} from '../types/pcmpAccounts.types';
import { pcmpSegmentClass } from '../utils/pcmpSegmentClass';

// Only members still waiting in the batch can be ticked: paying acts on those alone.
const redisburseBlockedReason = (item: PcmpRedisburseBatchItem) =>
  item.canRedisburse ? null : (item.redisburseBlockedReason ?? 'Not waiting to be paid.');

const VIEW_OPTIONS = [undefined, ...PCMP_ACCOUNTS_BATCH_VIEWS] as const;

/** Excel and print of one view's members (legacy Re-Disburse Pending / Re-Disbursed exports). */
function PcmpRedisburseExportButtons({
  batchNo,
  view,
}: {
  batchNo: number;
  view: PcmpAccountsBatchView;
}) {
  const xlsx = useDownloadPcmpBatchXlsx((date) =>
    downloadPcmpRedisburseBatchXlsx(batchNo, view, date),
  );
  const label = PCMP_REDISBURSE_VIEW_LABELS[view];

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => xlsx.mutate()}
        disabled={xlsx.isPending}
        className="gap-1.5"
      >
        {xlsx.isPending ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <FileSpreadsheet className="size-3.5" />
        )}
        Excel · {label}
      </Button>
      <Link
        to="/pcmp/accounts/redisburse-batches/$batchNo/print"
        params={{ batchNo }}
        search={{ view }}
        target="_blank"
        className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
      >
        <Printer className="size-3.5" />
        Print · {label}
      </Link>
    </>
  );
}

/** One re-disbursement batch: its members, paying them, and failing one before payout. */
export function PcmpRedisburseBatchPage() {
  const router = useRouter();
  const controller = usePcmpRedisburseBatchFilters();
  const { batchNo, params, appliedFilters, showAll, view, limit } = controller;
  const { can } = usePcmpPermissions();
  const canExport = can(PCMP_PERMISSION.VIEW_ALL, PCMP_PERMISSION.EXPORT);
  // A hint only: each row's capabilities and the API decide.
  const canAct = can(PCMP_PERMISSION.ACCOUNTS, PCMP_PERMISSION.ACT);

  const selection = usePcmpRowSelection(JSON.stringify([batchNo, appliedFilters, showAll, view]));
  const [bulkTarget, setBulkTarget] = useState<PcmpRedisburseBulkTarget | null>(null);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpRedisburseBatchQueryOptions(batchNo, params),
  );

  useKeepPageInRange(params.page, data?.proposals.meta.totalPages, controller.setPage);

  const rows = useMemo(() => data?.proposals.items ?? [], [data]);
  const batch = data?.batch;

  const openProposal = useCallback(
    (item: PcmpRedisburseBatchItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  const backButton = (
    <Link
      to="/pcmp/accounts"
      search={{ tab: batch?.pending.count === 0 ? 'REDISBURSED' : 'REDISBURSE_PENDING' }}
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit gap-1.5')}
    >
      <ArrowLeft className="size-3.5" />
      Re-disbursement batches
    </Link>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <PageLoader />
      </div>
    );
  }

  if (error || !data || !batch) {
    return (
      <div className="flex flex-col gap-5">
        {backButton}
        <ErrorState
          error={error}
          fallbackTitle={`Could not load re-disbursement batch #${batchNo}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  const canSelect = canAct && batch.pending.count > 0;
  const columns = getPcmpAccountsColumns<PcmpRedisburseBatchItem>({
    leading: canSelect
      ? [
          pcmpSelectColumn<PcmpRedisburseBatchItem>({
            selection,
            blockedReason: redisburseBlockedReason,
          }),
        ]
      : [],
    renderActions: (item) => (
      <>
        {canAct ? (
          <PcmpAccountsActionButtons
            proposalId={item.proposalId}
            refNo={item.refNo}
            fail={item.canFail ? { stage: item.failureStage } : null}
            canReturn={false}
            className="flex-nowrap"
          />
        ) : null}
        <PcmpHoldActionButtons
          proposalId={item.proposalId}
          refNo={item.refNo}
          canHold={item.canHold}
          canRelease={item.canRelease}
          hold={item.hold}
          className="flex-nowrap"
        />
        <Link
          to="/pcmp/proposals/$proposalId"
          params={{ proposalId: item.proposalId }}
          className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'gap-1.5')}
        >
          <Eye className="size-3.5" />
          View
        </Link>
      </>
    ),
  });

  // No pending total under filters from the API: "select all" covers the whole batch.
  const summary = selection.summarize(batch.pending.count, batch.pending.amount);
  const pageSelectable = rows.filter((row) => row.canRedisburse);
  const isPageSelected =
    pageSelectable.length > 0 &&
    pageSelectable.every((row) => selection.isSelected(row.proposalId));
  const refNoOf = (proposalId: number) => rows.find((row) => row.proposalId === proposalId)?.refNo;

  const disburseSelected = () =>
    setBulkTarget({
      action: 'DISBURSE',
      batchNo,
      count: summary.count,
      amount: summary.amount,
      scope: selection.allMatching ? 'All pending proposals in the batch' : 'Selected proposals',
      selection: selection.allMatching
        ? { selectAll: 'BATCH', excludedProposalIds: selection.pickedIds }
        : { proposalIds: selection.pickedIds },
    });

  const disburseWholeBatch = () =>
    setBulkTarget({
      action: 'DISBURSE',
      batchNo,
      count: batch.pending.count,
      amount: batch.pending.amount,
      scope: 'All pending proposals in the batch',
      selection: { selectAll: 'BATCH', excludedProposalIds: [] },
    });

  const exportViews = PCMP_ACCOUNTS_BATCH_VIEWS.filter((v) =>
    v === 'PENDING' ? batch.pending.count > 0 : batch.paid.count > 0,
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Up to two views' exports plus Disburse Batch: the actions take their own row. */}
      <div className="flex flex-col gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {backButton}
          <h1 className="text-2xl font-semibold">Re-disbursement batch #{batch.batchNo}</h1>
          {batch.parentBatchNo !== null ? (
            <span className="text-sm text-muted-foreground">from batch #{batch.parentBatchNo}</span>
          ) : null}
        </div>
        {(canExport && exportViews.length > 0) || canSelect ? (
          <div className="flex flex-wrap items-center gap-2">
            {canExport
              ? exportViews.map((v) => (
                  <PcmpRedisburseExportButtons key={v} batchNo={batchNo} view={v} />
                ))
              : null}
            {canSelect ? (
              <Button type="button" size="sm" onClick={disburseWholeBatch} className="gap-1.5">
                <Send className="size-3.5" />
                Disburse Batch ({formatNumber(batch.pending.count)})
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <PcmpBatchFact label="Proposals">
          <span className="text-lg font-semibold tabular-nums">
            {formatTaka(batch.totalAmount)}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatNumber(batch.itemCount)} {pluralize(batch.itemCount, 'proposal')}
          </span>
        </PcmpBatchFact>
        <PcmpBatchAmountFact label="Pending" value={batch.pending} />
        <PcmpBatchAmountFact label="Paid" value={batch.paid} />
        <PcmpBatchAmountFact label="Failed" value={batch.failed} />
        <PcmpBatchFact label="Created">
          <PcmpBatchStepCell by={batch.createdBy} at={batch.createdAt} />
        </PcmpBatchFact>
      </div>

      <div className="flex w-full max-w-full items-center overflow-x-auto rounded-lg border bg-muted/40 p-1 sm:w-fit">
        {VIEW_OPTIONS.map((option) => (
          <Button
            key={option ?? 'ALL'}
            type="button"
            variant="ghost"
            size="sm"
            aria-pressed={view === option}
            onClick={() => controller.setView(option)}
            className={pcmpSegmentClass(view === option)}
          >
            {option ? PCMP_REDISBURSE_VIEW_LABELS[option] : 'All'}
          </Button>
        ))}
      </div>

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-redisburse-batch" />

      {data.isFiltered || showAll ? (
        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id="pcmp-redisburse-batch-show-all"
            checked={showAll}
            onCheckedChange={(checked) => controller.setShowAll(checked)}
          />
          <Label htmlFor="pcmp-redisburse-batch-show-all" className="font-normal">
            Show all {formatNumber(batch.itemCount)} proposals in the batch, not only the{' '}
            {formatNumber(batch.matchedItemCount ?? data.proposals.meta.total)} matching the filters
          </Label>
        </div>
      ) : null}

      {canSelect ? (
        <PcmpSelectionBar
          selection={selection}
          summary={summary}
          selectAll={
            data.isFiltered
              ? undefined
              : { total: batch.pending.count, amount: batch.pending.amount, isPageSelected }
          }
          hint="Tick pending proposals to pay them to their corrected accounts."
        >
          <Button
            type="button"
            size="sm"
            onClick={disburseSelected}
            disabled={summary.count === 0}
            className="gap-1.5"
          >
            <Check className="size-3.5" />
            {PCMP_REDISBURSE_ACTION_COPY.DISBURSE.label} Selected
          </Button>
        </PcmpSelectionBar>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        isLoading={false}
        stickyFirstColumn
        className={cn(isFetching && 'opacity-70')}
        emptyIcon={Inbox}
        emptyMessage={
          data.isFiltered
            ? 'No proposals in this batch match the filters'
            : view
              ? `No ${PCMP_REDISBURSE_VIEW_LABELS[view].toLowerCase()} proposals in this batch`
              : 'This batch has no proposals'
        }
        skeletonRowCount={Math.min(limit, 15)}
        getRowClassName={(item) =>
          canSelect && item.canRedisburse && selection.isSelected(item.proposalId)
            ? 'bg-primary/5'
            : item.hold
              ? 'bg-warning/20'
              : undefined
        }
        onRowClick={openProposal}
      />

      <DataTablePagination
        page={data.proposals.meta.page}
        totalPages={data.proposals.meta.totalPages}
        total={data.proposals.meta.total}
        limit={data.proposals.meta.limit}
        onPageChange={controller.setPage}
        limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
      />

      {bulkTarget ? (
        <PcmpRedisburseBulkDialog
          target={bulkTarget}
          refNoOf={refNoOf}
          onDone={selection.clear}
          onClose={() => setBulkTarget(null)}
        />
      ) : null}
    </div>
  );
}
